<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex items-center justify-between px-3 py-2 border-b border-slate-600 flex-shrink-0">
      <span class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Audio Library</span>
      <div class="flex items-center gap-1">
        <label
          class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors cursor-pointer"
          title="Import a .wav file"
        >
          + File
          <input type="file" accept=".wav" class="hidden" @change="onFileSelected" />
        </label>
        <button
          type="button"
          class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors"
          title="Add an external audio stream URL"
          @click="showAddStream = !showAddStream"
        >+ Web</button>
        <button
          type="button"
          class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors"
          title="Add a reference to a Synth Library item"
          @click="toggleAddSynth"
        >+ Synth</button>
      </div>
    </div>

    <form v-if="showAddStream" class="px-3 py-3 border-b border-slate-700 space-y-2" @submit.prevent="onAddStream">
      <label for="audio-web-url" class="block text-xs text-slate-400">External audio URL</label>
      <div class="flex gap-2">
        <input
          id="audio-web-url"
          v-model="webUrl"
          type="url"
          required
          placeholder="https://radio.example.com/live.mp3"
          class="min-w-0 flex-1 text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          :disabled="addingStream"
        />
        <button
          type="submit"
          class="text-xs text-slate-900 bg-cyan-400 hover:bg-cyan-300 rounded px-3 py-1.5 disabled:opacity-50"
          :disabled="addingStream || !webUrl.trim()"
        >{{ addingStream ? 'Adding…' : 'Add' }}</button>
      </div>
      <input
        v-model="webName"
        type="text"
        placeholder="Resource name (optional)"
        class="w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500"
        :disabled="addingStream"
      />
      <p class="text-[11px] text-slate-500">Only the URL is saved. Preview streams through the Desktop app.</p>
    </form>

    <form v-if="showAddSynth" class="px-3 py-3 border-b border-slate-700 space-y-2" @submit.prevent="onAddSynth">
      <label for="audio-synth-resource" class="block text-xs text-slate-400">Synth Library resource</label>
      <div v-if="availableSynths.length" class="flex gap-2">
        <select
          id="audio-synth-resource"
          v-model="selectedSynthId"
          class="min-w-0 flex-1 text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-200 focus:outline-none focus:border-cyan-500"
        >
          <option v-for="synth in availableSynths" :key="synth.uuid" :value="synth.uuid">
            {{ synth.name }} · {{ synth.filename || 'no filename' }}
          </option>
        </select>
        <button type="submit" class="text-xs text-slate-900 bg-cyan-400 hover:bg-cyan-300 rounded px-3 py-1.5">Add</button>
      </div>
      <p v-else class="text-xs text-slate-500">{{ synthStore.all.length ? 'Every Synth Library item already has an Audio Resource.' : 'The Synth Library is empty.' }}</p>
      <p class="text-[11px] text-slate-500">Preview and deploy always use the current Synth Library version.</p>
    </form>

    <div v-if="importError" class="px-3 py-1.5 text-xs text-red-400 border-b border-slate-700">{{ importError }}</div>
    <div v-if="audioPreviewError" class="px-3 py-1.5 text-xs text-red-400 border-b border-slate-700">{{ audioPreviewError }}</div>

    <ResourceListToolbar
      v-if="clips.length > 0"
      :search="search"
      @update:search="search = $event"
      :sortKey="sortKey"
      @update:sortKey="sortKey = $event"
      :sortOptions="sortOptions"
    />

    <div class="flex-1 overflow-y-auto">
      <div v-if="clips.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No audio yet.<br>
        Click <strong class="text-slate-400">+ File</strong> for a local WAV, <strong class="text-slate-400">+ Web</strong> for a stream, or <strong class="text-slate-400">+ Synth</strong> for a Synth Library reference.
      </div>
      <div v-else-if="filteredClips.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No audio matches your search.
      </div>

      <template v-for="c in filteredClips" :key="c.uuid">
        <ResourceListItem
          :title="displayName(c)"
          :badge="resourceBadge(c)"
          :meta="rowMeta(c)"
          :used="isUsed(c)"
          @select="editingUUID = editingUUID === c.uuid ? null : c.uuid"
        >
          <template #actions>
            <button
              class="text-slate-400 hover:text-cyan-300 p-1 rounded hover:bg-slate-600 transition-colors"
              :class="activeAudioPreviewUuid === c.uuid ? 'text-red-400 hover:text-red-300' : ''"
              :title="activeAudioPreviewUuid === c.uuid ? 'Stop preview' : 'Play preview'"
              @click="togglePreview(c)"
            >
              <svg v-if="activeAudioPreviewUuid === c.uuid" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 6h12v12H6z" />
              </svg>
              <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              class="text-slate-400 hover:text-red-400 p-1 rounded hover:bg-slate-600 transition-colors"
              title="Delete"
              @click="pendingDeleteUUID = c.uuid"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </template>
        </ResourceListItem>

        <!-- Inline editor: display-name rename + the read-only identities.
             Kept inline rather than in a FloatingPopup-hosted editor (the
             Font/Dataset pattern) because there are only three fields and one
             is editable — a popup would be more chrome than content. -->
        <div v-if="editingUUID === c.uuid" class="px-3 py-3 bg-slate-700/40 border-b border-slate-700 space-y-3">
          <div v-if="!isSynthAudio(c)">
            <form-label class="text-xs text-slate-400 mb-1">Name</form-label>
            <form-input
              class="w-full text-sm"
              :modelValue="c.name"
              placeholder="Audio name"
              @input="store.updateAudio(c.uuid, { name: $event.target.value })"
            />
          </div>
          <div v-if="isSynthAudio(c)">
            <form-label class="text-xs text-slate-400 mb-1">Synth Library source</form-label>
            <div class="text-xs text-slate-300 bg-slate-800/60 rounded px-2 py-1.5 border border-slate-700">
              {{ sourceSynth(c)?.name || `${c.name} (missing synth)` }}
            </div>
            <form-label class="text-xs text-slate-400 mt-2 mb-1">Deployment file</form-label>
            <div class="font-mono text-xs text-slate-400 bg-slate-800/60 rounded px-2 py-1.5 border border-slate-700">{{ devicePath(c) }}</div>
          </div>
          <div v-else-if="isUrlAudio(c)">
            <form-label class="text-xs text-slate-400 mb-1">External URL</form-label>
            <div class="font-mono text-xs text-slate-400 bg-slate-800/60 rounded px-2 py-1.5 border border-slate-700 select-all break-all"
              title="This original URL is retained in the project and sent directly to firmware">{{ c.url }}</div>
          </div>
          <div v-else>
            <form-label class="text-xs text-slate-400 mb-1">Device file</form-label>
            <div class="font-mono text-xs text-slate-400 bg-slate-800/60 rounded px-2 py-1.5 border border-slate-700 select-all break-all"
              title="Uploaded to the plate at this path, and what the audio action resolves to">{{ devicePath(c) }}</div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <ConfirmationModal
    :show="pendingDeleteUUID !== null"
    title="Delete Audio"
    :message="pendingDeleteClip ? `Are you sure you want to delete &lt;strong&gt;${displayName(pendingDeleteClip)}&lt;/strong&gt;?` : 'Are you sure?'"
    @confirm="confirmDelete"
    @cancel="pendingDeleteUUID = null"
  />
</template>

<script setup>
/**
 * AudioManager.vue — the Audio entry in the Resources section, modeled on
 * FontManager.vue (same ResourceListToolbar + ResourceListItem +
 * ConfirmationModal shell, same search/sort, same "used in design" badge).
 *
 * Differences from FontManager are only the ones the resource itself forces:
 * there is no "+ New then attach bytes" flow (an audio resource without bytes
 * has no meaning, so importing a local WAV or adding URL metadata creates the
 * resource), and the editor is inline rather than a FloatingPopup.
 *
 * A widget references an audio resource per action event — the operand of a
 * typed `audio.play` action in node.actions (see ConfigManager) — so usage
 * lookups go through findWidgetsUsingActionResource(), not the scalar-property
 * walker images and fonts use.
 */
import { ref, computed, onBeforeUnmount } from 'vue'
import { useAudioStore, audioDevicePath, defaultAudioStreamName, AUDIO_URL_RESOURCE_TYPE, AUDIO_SYNTH_RESOURCE_TYPE } from '../../stores/audioStore.js'
import { useSynthStore } from '../../stores/synthStore.js'
import { synthDeviceFilename } from '../../services/synthDeploy.js'
import { activeAudioPreviewUuid, audioPreviewError, previewAudio, stopAudioPreview } from '../../services/audioPreview.js'
import { findWidgetsUsingActionResource } from '../../utils/resourceUsage.js'
import ConfirmationModal from '../ConfirmationModal.vue'
import ResourceListToolbar from '../resources/ResourceListToolbar.vue'
import ResourceListItem from '../resources/ResourceListItem.vue'

const props = defineProps({
  objectlayer: { type: Object, default: null },
})

const store = useAudioStore()
const synthStore = useSynthStore()
const clips = computed(() => store.all)
const importError = ref('')
const showAddStream = ref(false)
const webUrl = ref('')
const webName = ref('')
const addingStream = ref(false)
const showAddSynth = ref(false)
const selectedSynthId = ref('')
const availableSynths = computed(() => synthStore.all.filter(synth => !store.getAudioBySynthId(synth.uuid)))

// --- Search / sort ---

const search = ref('')
const sortKey = ref('name-asc')

const sortOptions = [
  { value: 'name-asc',  label: 'Name ↑' },
  { value: 'name-desc', label: 'Name ↓' },
]

const filteredClips = computed(() => {
  let list = clips.value
  const q = search.value.trim().toLowerCase()
  if (q) {
    list = list.filter(c => displayName(c).toLowerCase().includes(q) || (c.filename ?? '').toLowerCase().includes(q) || (c.url ?? '').toLowerCase().includes(q))
  }
  list = [...list]
  switch (sortKey.value) {
    case 'name-asc':  list.sort((a, b) => displayName(a).localeCompare(displayName(b))); break
    case 'name-desc': list.sort((a, b) => displayName(b).localeCompare(displayName(a))); break
  }
  return list
})

const devicePath = (c) => {
  if (isSynthAudio(c)) {
    const filename = synthDeviceFilename(sourceSynth(c))
    return filename ? `L:/${filename}` : 'Missing synth'
  }
  return audioDevicePath(c) ?? '—'
}

function sourceSynth(c) {
  return isSynthAudio(c) ? synthStore.getSynth(c.synthId) : null
}

function displayName(c) {
  return sourceSynth(c)?.name ?? c.name
}

function refCount(c) {
  return props.objectlayer ? findWidgetsUsingActionResource(props.objectlayer, c.uuid).length : 0
}

function rowMeta(c) {
  const refs = refCount(c)
  if (isUrlAudio(c)) return [c.url, `${refs} reference${refs === 1 ? '' : 's'}`]
  if (isSynthAudio(c)) return [devicePath(c), `Synth Library · ${refs} reference${refs === 1 ? '' : 's'}`]
  const parts = [c.filename]
  if (c.metadata?.fileSize) parts.push(formatSize(c.metadata.fileSize))
  parts.push(`${refs} reference${refs === 1 ? '' : 's'}`)
  return parts
}

function isUrlAudio(c) {
  return c.type === AUDIO_URL_RESOURCE_TYPE
}

function isSynthAudio(c) {
  return c.type === AUDIO_SYNTH_RESOURCE_TYPE
}

function resourceBadge(c) {
  if (isUrlAudio(c)) return 'Web stream'
  if (isSynthAudio(c)) return 'Synth'
  return ''
}

function isUsed(c) {
  return refCount(c) > 0
}

function formatSize(size) {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

// --- Import / preview / delete ---

const editingUUID = ref(null)

async function onFileSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return
  importError.value = ''
  try {
    editingUUID.value = await store.importWavFile(file)
  } catch (e) {
    importError.value = e.message || 'Failed to import that file.'
  }
  event.target.value = '' // let the same file be re-picked
}

async function onAddStream() {
  importError.value = ''
  addingStream.value = true
  try {
    editingUUID.value = store.addAudioUrlResource({
      url: webUrl.value.trim(),
      name: webName.value.trim() || defaultAudioStreamName(webUrl.value.trim()),
    })
    webUrl.value = ''
    webName.value = ''
    showAddStream.value = false
  } catch (e) {
    importError.value = e.message || 'Failed to add that audio URL.'
  } finally {
    addingStream.value = false
  }
}

function toggleAddSynth() {
  showAddSynth.value = !showAddSynth.value
  if (showAddSynth.value && !selectedSynthId.value) {
    selectedSynthId.value = availableSynths.value[0]?.uuid ?? ''
  }
}

function onAddSynth() {
  importError.value = ''
  const synth = synthStore.getSynth(selectedSynthId.value)
  if (!synth) {
    importError.value = 'Choose an existing synth first.'
    return
  }
  try {
    editingUUID.value = store.addSynthAudioResource({ name: synth.name, synthId: synth.uuid })
    selectedSynthId.value = availableSynths.value[0]?.uuid ?? ''
    showAddSynth.value = false
  } catch (e) {
    importError.value = e.message || 'Failed to add that synth reference.'
  }
}

async function togglePreview(c) {
  importError.value = ''
  try {
    if (activeAudioPreviewUuid.value === c.uuid) await stopAudioPreview()
    else await previewAudio(c)
  } catch (e) {
    importError.value = e.message || 'Audio preview failed.'
  }
}

onBeforeUnmount(() => { void stopAudioPreview().catch(() => {}) })

const pendingDeleteUUID = ref(null)
const pendingDeleteClip = computed(() => pendingDeleteUUID.value ? store.getAudio(pendingDeleteUUID.value) : null)

async function confirmDelete() {
  if (pendingDeleteUUID.value) {
    if (activeAudioPreviewUuid.value === pendingDeleteUUID.value) await stopAudioPreview()
    if (editingUUID.value === pendingDeleteUUID.value) editingUUID.value = null
    store.removeAudio(pendingDeleteUUID.value)
  }
  pendingDeleteUUID.value = null
}
</script>
